import React, { useState, useRef, useEffect } from 'react';
import TorusAnimation from './TorusAnimation';
import './SelflessMode.css';

const GESTURE_THRESHOLD = 40; // px
const MAX_TAP_DISTANCE = 15; // px

const SelflessMode = ({ onClose }) => {
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoBlack, setIsVideoBlack] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [fadeOutOpacity, setFadeOutOpacity] = useState(0);
  const [peers, setPeers] = useState([]);

  const gestureStart = useRef({ x: 0, y: 0, t: 0 });
  const lastTapRef = useRef(0);
  const rafRef = useRef(null);
  const videoElsRef = useRef([]);

  // Keep references to peer video elements
  useEffect(() => {
    videoElsRef.current = Array.from(document.querySelectorAll('.peer-video'));
  }, [peers]);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const handleHangupFade = () => {
    const start = performance.now();
    const duration = 1600;

    const step = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setFadeOutOpacity(p);
      // Reduce remote audio volumes progressively
      videoElsRef.current.forEach((v) => {
        try {
          const remaining = Math.max(0, 1 - p);
          v.volume = remaining;
        } catch (_) {}
      });
      if (p < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        onClose();
      }
    };

    rafRef.current = requestAnimationFrame(step);
  };

  const toggleMute = () => {
    setIsMuted((prev) => !prev);
    // If a local audio track exists in a future integration, disable/enable it here.
  };

  const toggleBlackScreen = () => {
    setIsVideoBlack((prev) => !prev);
    // Black overlay remains gesture-interactive to allow toggling back or other actions
  };

  const openShareMenu = () => {
    setShowContacts(false);
    setShowShareMenu(true);
  };

  const openContacts = () => {
    setShowShareMenu(false);
    setShowContacts(true);
  };

  const closeOverlays = () => {
    setShowShareMenu(false);
    setShowContacts(false);
  };

  // Gesture detection layer (mouse + touch)
  const onPointerDown = (clientX, clientY) => {
    gestureStart.current = { x: clientX, y: clientY, t: Date.now() };
  };

  const onPointerUp = (clientX, clientY) => {
    const dx = clientX - gestureStart.current.x;
    const dy = clientY - gestureStart.current.y;
    const dist = Math.hypot(dx, dy);

    // Double-tap to exit black screen
    const now = Date.now();
    if (isVideoBlack && dist <= MAX_TAP_DISTANCE) {
      if (now - lastTapRef.current < 350) {
        setIsVideoBlack(false);
        lastTapRef.current = 0;
        return;
      }
      lastTapRef.current = now;
      return; // Single tap on black screen does nothing (wait for second tap)
    }

    if (dist <= MAX_TAP_DISTANCE) {
      // Tap on geometry layer → toggle mute
      toggleMute();
      return;
    }

    // Determine dominant axis
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > GESTURE_THRESHOLD) {
      if (dx < 0) {
        // Slide left → black screen toggle
        toggleBlackScreen();
      } else {
        // Slide right → hang up fade
        handleHangupFade();
      }
      return;
    }

    if (Math.abs(dy) > GESTURE_THRESHOLD) {
      if (dy < 0) {
        // Slide up → share submenu
        openShareMenu();
      } else {
        // Slide down → contacts panel
        openContacts();
      }
    }
  };

  const handleMouseDown = (e) => onPointerDown(e.clientX, e.clientY);
  const handleMouseUp = (e) => onPointerUp(e.clientX, e.clientY);

  const handleTouchStart = (e) => {
    if (!e.touches || e.touches.length === 0) return;
    const t = e.touches[0];
    onPointerDown(t.clientX, t.clientY);
  };
  const handleTouchEnd = (e) => {
    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    onPointerUp(t.clientX, t.clientY);
  };

  // Share actions
  const fileInputImageRef = useRef(null);
  const fileInputAttachmentRef = useRef(null);

  const handleShareText = async () => {
    const text = 'Partage via Selfless mode';
    if (navigator.share) {
      try {
        await navigator.share({ text });
      } catch (_) {}
    }
  };

  const handleShareImage = () => {
    if (fileInputImageRef.current) fileInputImageRef.current.click();
  };

  const handleShareAttachment = () => {
    if (fileInputAttachmentRef.current) fileInputAttachmentRef.current.click();
  };

  const onPickImage = () => {
    setShowShareMenu(false);
  };

  const onPickAttachment = () => {
    setShowShareMenu(false);
  };

  return (
    <div className="selfless-mode">
      <div className="media-stage">
        {/* Remote peers grid (no self-view) */}
        <div className="video-grid">
          {peers.length === 0 ? (
            <div className="waiting-peers">
              <p>En attente de participants…</p>
            </div>
          ) : (
            peers.map((peer) => (
              <video
                key={peer.id}
                className="peer-video"
                autoPlay
                playsInline
                ref={(el) => {
                  if (el && peer.stream) {
                    try { el.srcObject = peer.stream; } catch (_) {}
                  }
                }}
              />
            ))
          )}
        </div>

        {/* Torus replaces buttons; input capture via gesture layer */}
        <div className={`torus-overlay ${isMuted ? 'muted' : ''}`}>
          <TorusAnimation disablePopup={true} />
        </div>

        {/* Share submenu anchored to torus (separate anchor to remain clickable) */}
        {showShareMenu && (
          <div className="torus-share-anchor">
            <div className="share-menu" onClick={closeOverlays}>
              <div className="share-option share-text" onClick={(e) => { e.stopPropagation(); handleShareText(); }} title="Partager du texte">Aa</div>
              <div className="share-option share-image" onClick={(e) => { e.stopPropagation(); handleShareImage(); }} title="Partager une image">🖼️</div>
              <div className="share-option share-attach" onClick={(e) => { e.stopPropagation(); handleShareAttachment(); }} title="Partager une pièce jointe">📎</div>
              <input type="file" accept="image/*" ref={fileInputImageRef} style={{ display: 'none' }} onChange={onPickImage} />
              <input type="file" ref={fileInputAttachmentRef} style={{ display: 'none' }} onChange={onPickAttachment} />
            </div>
          </div>
        )}

        {/* Gesture layer (full screen) */}
        <div
          className="gesture-layer"
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onClick={(e) => e.preventDefault()}
        />

        {/* Contacts panel (slide up from bottom) */}
        {showContacts && (
          <div className="contacts-panel" role="dialog" aria-modal="true">
            <div className="contacts-header">
              <span>Contacts</span>
              <button className="contacts-close" onClick={() => setShowContacts(false)} aria-label="Fermer">✕</button>
            </div>
            <div className="contacts-body">
              <div className="contacts-empty">Aucun contact disponible</div>
            </div>
          </div>
        )}

        {/* Black screen overlay (keeps gestures active) */}
        {isVideoBlack && <div className="black-overlay" />}

        {/* Hangup fade overlay */}
        {fadeOutOpacity > 0 && (
          <div className="hangup-fade" style={{ opacity: fadeOutOpacity }} />
        )}
      </div>
    </div>
  );
};

export default SelflessMode;
